<template>
  <div>
    <b-alert
      v-if="apiError"
      show
      variant="danger"
      class="p-1"
    >
      {{ apiError }}
    </b-alert>

    <template v-if="isCreateMode">
      <external-partner-form
        v-model="form"
        :loading="loading || saving || deleting"
        :create-mode="isCreateMode"
      />

      <div class="d-flex flex-wrap gap-1 mt-1 mb-2">
        <b-button
          variant="primary"
          :disabled="loading || saving || deleting"
          @click="save"
        >
          {{ $t('partner-admin.actions.create') }}
        </b-button>
      </div>
    </template>

    <b-tabs
      v-else-if="partnerId"
      justified
      lazy
      pills
    >
      <b-tab :title="$t('partner-admin.tabs.basic-info')">
        <external-partner-form
          v-model="form"
          :loading="loading || saving || deleting"
          :create-mode="false"
        />

        <div class="d-flex flex-wrap gap-1 mt-1 mb-2">
          <b-button
            variant="primary"
            :disabled="loading || saving || deleting"
            @click="save"
          >
            {{ $t('partner-admin.actions.save') }}
          </b-button>
          <b-button
            variant="outline-danger"
            :disabled="loading || saving || deleting"
            @click="deletePartner"
          >
            {{ $t('partner-admin.actions.delete') }}
          </b-button>
        </div>
      </b-tab>

      <b-tab :title="$t('partner-admin.tabs.users')">
        <external-partner-users-section
          :key="`users-${refreshKey}`"
          :partner-id="partnerId"
          :loading="loading || saving || deleting"
          class="mb-2"
        />
      </b-tab>

      <b-tab :title="$t('partner-admin.tabs.customers')">
        <external-partner-mappings-section
          :key="`mappings-${refreshKey}`"
          :partner-id="partnerId"
          :loading="loading || saving || deleting"
        />
      </b-tab>
    </b-tabs>

    <div
      v-if="!isCreateMode && partnerId"
      class="d-flex justify-content-end mt-1"
    >
      <b-button
        variant="flat-secondary"
        :to="{ name: 'external-partner-list' }"
      >
        {{ $t('partner-admin.actions.back-to-list') }}
      </b-button>
    </div>
  </div>
</template>

<script>
import {
  BAlert,
  BButton,
  BTab,
  BTabs,
} from 'bootstrap-vue'
import { parseRequestError } from '@/helpers/helpers'
import ExternalPartnerForm from './components/ExternalPartnerForm.vue'
import ExternalPartnerUsersSection from './components/ExternalPartnerUsersSection.vue'
import ExternalPartnerMappingsSection from './components/ExternalPartnerMappingsSection.vue'

const createEmptyForm = () => ({
  name: '',
  code: '',
  status: 'active',
  allowedOrderTypes: [],
  orderRequestInstruction: '',
  settings: {},
})

export default {
  name: 'ExternalPartnerEdit',
  components: {
    BAlert,
    BButton,
    BTab,
    BTabs,
    ExternalPartnerForm,
    ExternalPartnerUsersSection,
    ExternalPartnerMappingsSection,
  },
  data() {
    return {
      loading: false,
      saving: false,
      deleting: false,
      apiError: null,
      form: createEmptyForm(),
      refreshKey: 0,
    }
  },
  computed: {
    isCreateMode() {
      return this.$route.name === 'external-partner-create'
    },
    partnerId() {
      const value = Number(this.$route.params.id)
      if (!Number.isInteger(value) || value <= 0) {
        return null
      }

      return value
    },
  },
  watch: {
    '$route.fullPath': {
      handler() {
        this.initializePage()
      },
    },
  },
  created() {
    this.initializePage()
  },
  methods: {
    async initializePage() {
      this.loading = true
      this.apiError = null
      this.form = createEmptyForm()

      if (this.isCreateMode || !this.partnerId) {
        this.loading = false
        return
      }

      try {
        const response = await this.$store.dispatch('externalPartner/fetchPartnerById', this.partnerId)
        const partner = response.data
        this.form = {
          name: partner.name || '',
          code: partner.code || '',
          status: partner.status || 'active',
          allowedOrderTypes: partner.allowedOrderTypes || [],
          orderRequestInstruction: partner.orderRequestInstruction || '',
          settings: partner.settings || {},
        }
      } catch (error) {
        parseRequestError(error)
        this.apiError = error?.response?.data?.message || this.$t('partner-admin.errors.fetch-details')
      } finally {
        this.loading = false
      }
    },
    validateForm() {
      if (!this.form.name || !this.form.code) {
        this.apiError = this.$t('partner-admin.errors.required-fields')
        return false
      }

      return true
    },
    async save() {
      this.apiError = null
      if (!this.validateForm()) {
        return
      }

      this.saving = true

      const payload = {
        name: this.form.name,
        code: this.form.code,
        status: this.form.status,
        allowedOrderTypes: this.form.allowedOrderTypes,
        orderRequestInstruction: this.form.orderRequestInstruction,
        settings: this.form.settings,
      }

      try {
        if (this.isCreateMode) {
          const response = await this.$store.dispatch('externalPartner/createPartner', payload)
          window.toast.notify.success(this.$t('partner-admin.notifications.created'))
          this.$router.push({
            name: 'external-partner-details',
            params: { id: response.data.id },
          })
          return
        }

        await this.$store.dispatch('externalPartner/updatePartner', {
          id: this.partnerId,
          data: payload,
        })
        window.toast.notify.success(this.$t('partner-admin.notifications.saved'))
        this.refreshKey += 1
      } catch (error) {
        parseRequestError(error)
        this.apiError = error?.response?.data?.message || this.$t('partner-admin.errors.save')
      } finally {
        this.saving = false
      }
    },
    async deletePartner() {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        this.$t('partner-admin.confirm.delete-partner'),
        {
          title: this.$t('partner-admin.actions.delete'),
          okTitle: this.$t('shared.yes'),
          cancelTitle: this.$t('shared.no'),
          centered: true,
          okVariant: 'danger',
          cancelVariant: 'secondary',
        },
      )

      if (!confirmed) {
        return
      }

      this.deleting = true
      this.apiError = null

      try {
        await this.$store.dispatch('externalPartner/deletePartner', this.partnerId)
        this.$router.push({ name: 'external-partner-list' })
      } catch (error) {
        parseRequestError(error)
        this.apiError = error?.response?.data?.message || this.$t('partner-admin.errors.delete-partner')
      } finally {
        this.deleting = false
      }
    },
  },
}
</script>

<style scoped lang="scss">
.gap-1 {
  gap: 0.5rem;
}
</style>
